import React from 'react';

/**
 * Simple markdown-like text renderer.
 * Supports: **bold**, *italic*, [links](url), `code`, - bullet lists, numbered lists, line breaks.
 * No external dependencies — lightweight and safe (no dangerouslySetInnerHTML for user content).
 */
export default function MarkdownText({ text = '', style = {} }) {
  if (!text) return null;

  const blocks = text.split(/\n\n+/); // Split by blank lines into paragraphs

  return (
    <div style={{ lineHeight: 1.75, textAlign: 'justify', ...style }}>
      {blocks.map((block, blockIdx) => {
        const trimmed = block.trim();
        if (!trimmed) return null;

        // Check if it's a list block (all lines start with - or * or 1.)
        const lines = trimmed.split('\n');
        const isBulletList = lines.every(l => /^\s*[-*]\s/.test(l));
        const isNumberedList = lines.every(l => /^\s*\d+[.)]\s/.test(l));

        if (isBulletList) {
          return (
            <ul key={blockIdx} style={{ margin: '0.5rem 0', paddingLeft: '1.5rem', listStyle: 'disc' }}>
              {lines.map((line, i) => (
                <li key={i} style={{ marginBottom: '0.25rem' }}>
                  <InlineMarkdown text={line.replace(/^\s*[-*]\s/, '')} />
                </li>
              ))}
            </ul>
          );
        }

        if (isNumberedList) {
          return (
            <ol key={blockIdx} style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
              {lines.map((line, i) => (
                <li key={i} style={{ marginBottom: '0.25rem' }}>
                  <InlineMarkdown text={line.replace(/^\s*\d+[.)]\s/, '')} />
                </li>
              ))}
            </ol>
          );
        }

        // Check if it's a heading
        const headingMatch = trimmed.match(/^(#{1,3})\s+(.+)/);
        if (headingMatch) {
          const level = headingMatch[1].length;
          const HeadingTag = `h${level + 1}`; // h2, h3, h4
          const sizes = { 1: '1.3rem', 2: '1.15rem', 3: '1rem' };
          return (
            <HeadingTag key={blockIdx} style={{
              fontSize: sizes[level], fontWeight: 700, marginTop: '1rem', marginBottom: '0.4rem',
            }}>
              <InlineMarkdown text={headingMatch[2]} />
            </HeadingTag>
          );
        }

        // Regular paragraph — handle single newlines as <br>
        return (
          <p key={blockIdx} style={{ margin: '0.4rem 0' }}>
            {lines.map((line, i) => (
              <React.Fragment key={i}>
                {i > 0 && <br />}
                <InlineMarkdown text={line} />
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}

/**
 * Renders inline markdown: **bold**, *italic*, `code`, [link](url)
 */
function InlineMarkdown({ text }) {
  if (!text) return null;

  // Regex to match inline patterns
  const parts = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    // Try to match the earliest pattern
    const patterns = [
      { regex: /\*\*(.+?)\*\*/, type: 'bold' },
      { regex: /\*(.+?)\*/, type: 'italic' },
      { regex: /`(.+?)`/, type: 'code' },
      { regex: /\[(.+?)\]\((.+?)\)/, type: 'link' },
    ];

    let earliest = null;
    let earliestIdx = remaining.length;
    let earliestMatch = null;

    for (const p of patterns) {
      const match = remaining.match(p.regex);
      if (match && match.index < earliestIdx) {
        earliest = p;
        earliestIdx = match.index;
        earliestMatch = match;
      }
    }

    if (!earliest) {
      // No more patterns — push rest as text
      parts.push(<React.Fragment key={key++}>{remaining}</React.Fragment>);
      break;
    }

    // Push text before the match
    if (earliestIdx > 0) {
      parts.push(<React.Fragment key={key++}>{remaining.substring(0, earliestIdx)}</React.Fragment>);
    }

    // Push the matched element
    switch (earliest.type) {
      case 'bold':
        parts.push(<strong key={key++}>{earliestMatch[1]}</strong>);
        break;
      case 'italic':
        parts.push(<em key={key++}>{earliestMatch[1]}</em>);
        break;
      case 'code':
        parts.push(
          <code key={key++} style={{
            background: 'rgba(108,92,231,0.1)', padding: '0.15rem 0.4rem',
            borderRadius: '4px', fontSize: '0.88em', fontFamily: 'monospace',
          }}>
            {earliestMatch[1]}
          </code>
        );
        break;
      case 'link':
        parts.push(
          <a key={key++} href={earliestMatch[2]} target="_blank" rel="noopener noreferrer"
            style={{ color: 'var(--primary)', textDecoration: 'underline', fontWeight: 500 }}>
            {earliestMatch[1]}
          </a>
        );
        break;
      default:
        break;
    }

    remaining = remaining.substring(earliestIdx + earliestMatch[0].length);
  }

  return <>{parts}</>;
}
